import { Alert, Button, Skeleton } from '@design-system';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  PaymentMethodCreateParams,
  SetupIntentResult,
  StripeError,
} from '@stripe/stripe-js';
import { customsCodeIsRequired } from '@utils/address';
import { TrackGtagEvent } from '@utils/analytics';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { AnalyticsFlowProvider } from '@utils/analytics/flow';
import { useConvertCurrency } from '@utils/currencyConverter';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomsCode } from '../../../components/Containers/ReviewCheckoutDrawer/PaymentMethod/CustomsCode';
import {
  PaymentMethodButton,
  PaymentType,
} from '../../../components/Containers/ReviewCheckoutDrawer/PaymentMethod/PaymentMethodButton';
// Components
import { ReviewItem } from '../../../components/Containers/ReviewCheckoutDrawer/ReviewItem';
import { ShippingAddressButton } from '../../../components/Containers/ReviewCheckoutDrawer/ShippingAddress/ShippingAddressButton';
import { ProductPrice } from '../../../components/ProductPrice/ProductPrice';
import { AML_LIMIT } from '../../../constants/product';
import { useDialogContext } from '../../../hooks/useDialogContext';
import { useAuth } from '../../../providers/AuthProvider';
import { useFlow } from '../../../providers/FlowProvider';
// Constants
// Hooks
import { usePreAuthOfferCtx } from '../../../providers/PreAuthOfferProvider';
import { usePurchase } from '../../../providers/PurchaseProvider';
import { ProductV2Storyblok } from '../../../types/generated-storyblok';
// Types
import { captureError, captureErrorMessage } from '../../../utils/capture';
// Utils
import { getBackgroundImage } from '../../draws/utils/images';
import { ConfirmationDialog } from './ConfirmationDialog';

type Props = {
  product: ProductType;
};

export const ReviewStep = ({ product }: Props) => {
  const purchasingController = usePurchase();
  const { t } = useTranslation(['product']);
  const elements = useElements();
  const stripe = useStripe();
  const { user } = useAuth();
  const { closeFlow, goToStep } = useFlow();
  const {
    addresses,
    billingAddress,
    data,
    error,
    handleConfirmOffer,
    isBillingSameAsShipping,
    isNewPaymentMethod,
    loading,
    paymentMethods,
    selectedAddressId,
    selectedPaymentMethodId,
    setError,
    setupSecret,
  } = usePreAuthOfferCtx();
  const { hideDialog, showDialog } = useDialogContext();
  const [emitter] = useAnalyticsEmitter();
  const currencyConverter = useConvertCurrency();

  const [working, setWorking] = useState(false);

  const paymentReady = Boolean(selectedPaymentMethodId || isNewPaymentMethod);
  const workingOrLoading = working || loading;

  const selectedAddress = addresses?.addresses.find(
    (a) => a.id === selectedAddressId,
  );

  const isCustomsCodeMissing =
    customsCodeIsRequired(selectedAddress?.countryCode) &&
    !selectedAddress?.customsCode;

  const readyToConfirm =
    paymentReady &&
    !workingOrLoading &&
    selectedAddressId &&
    !isCustomsCodeMissing;

  const eventProperties = useMemo(
    () => ({
      'artist_id': product.productArtist?.map(
        (a: ResolvedRelatedArtist) => a.artist.internalId,
      )?.[0],
      'offer_id': data?.id,
      'product_id': product.id[0].id,
      'product_name': product.title,
      'user_id': user?.id,
    }),
    [product, user, data?.id],
  );

  useEffect(() => {
    return () => {
      setError(undefined);
    };
  }, [setError]);

  const showSuccessDialog = useCallback(() => {
    closeFlow();

    let isAmlRequired = false;
    if (data?.offer.amount) {
      const limit = currencyConverter.convert(
        AML_LIMIT,
        data.offer.currencyCode,
      );

      isAmlRequired = Number(data.offer.amount) > limit;
    }

    showDialog(
      <AnalyticsFlowProvider
        name="offer success"
        properties={{
          'payment method': 'card',
        }}
      >
        {() => (
          <ConfirmationDialog
            backgroundImage={getBackgroundImage(product)}
            isAmlRequired={isAmlRequired}
            offer={data?.offer}
            onClose={hideDialog}
            productTitle={product.title}
          />
        )}
      </AnalyticsFlowProvider>,
    );

    emitter('success', '', eventProperties);

    TrackGtagEvent('offer', eventProperties);
  }, [
    hideDialog,
    emitter,
    eventProperties,
    product,
    data?.offer,
    closeFlow,
    showDialog,
    currencyConverter,
  ]);

  async function handleStripeResponse(
    response: SetupIntentResult | { error: StripeError },
  ) {
    if (
      (!response.error ||
        (response.error.setup_intent?.status === 'succeeded' &&
          response.error.code === 'setup_intent_unexpected_state')) &&
      product.shopifyVariantId
    ) {
      const isConfirmed = await handleConfirmOffer();
      if (isConfirmed) {
        showSuccessDialog();
      }
      return;
    }

    if (response.error) {
      setError(response.error.code || response.error.type);
      captureError(response.error);
    }
  }

  async function handleSubmitOffer() {
    if (!stripe || !elements) {
      setError(t('drawers.offer.errors.generic'));
      captureErrorMessage('Stripe SDK is not setup');
      return;
    }

    setError(undefined);
    setWorking(true);

    try {
      const url = new URL(window.location.href);

      if (selectedPaymentMethodId && setupSecret) {
        const response = await stripe.confirmSetup({
          clientSecret: setupSecret,
          confirmParams: {
            return_url: url.toString(),
          },
          redirect: 'if_required',
        });

        // If we get here, it means 3DS was handled in a modal
        handleStripeResponse(response);
        return;
      }

      // Creating new payment method
      let billingDetails: PaymentMethodCreateParams.BillingDetails | undefined;

      if (isNewPaymentMethod && isBillingSameAsShipping) {
        if (!selectedAddress) throw new Error('No shipping address selected');

        billingDetails = {
          name: [selectedAddress.firstName, selectedAddress.lastName].join(' '),
          phone: selectedAddress.phone ?? undefined,
          email: user?.email ?? '',
          address: {
            line1: selectedAddress.addressLine1 ?? '',
            line2: selectedAddress.addressLine2 ?? '',
            city: selectedAddress.city ?? '',
            country: selectedAddress.countryCode ?? '',
            postal_code: selectedAddress.zip ?? '',
            state: selectedAddress.provinceName ?? '',
          },
        };
      } else {
        billingDetails = {
          name: billingAddress?.name ?? '',
          phone: billingAddress?.phone ?? '',
          email: user?.email ?? '',
          address: {
            line1: billingAddress?.address?.line1 ?? '',
            line2: billingAddress?.address?.line2 ?? '',
            city: billingAddress?.address?.city ?? '',
            country: billingAddress?.address?.country ?? '',
            postal_code: billingAddress?.address?.postal_code ?? '',
            state: billingAddress?.address?.state ?? '',
          },
        };
      }

      const response = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: url.toString(),
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      // If we get here, it means 3DS was handled in a modal
      handleStripeResponse(response);
    } catch (err) {
      setError(t('drawers.offer.errors.payment'));
      captureError(err as Error);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex flex-col space-y-6">
      {error && (
        <div className="w-full flex-1">
          <Alert status="error">{error}</Alert>
        </div>
      )}
      <div className="flex w-full flex-col space-y-4">
        <div
          className={classNames(
            'flex flex-col space-y-4 rounded-sm border border-divider/20 p-4',
          )}
        >
          <ReviewItem
            allowMultiples={false}
            artist={purchasingController.product?.artist ?? ''}
            disabled={false}
            imageFocus={
              product.component === 'product_v2'
                ? ((product as unknown as ProductV2Storyblok).ogImage?.focus ??
                  '')
                : (product.thumbnail?.focus ?? '')
            }
            imageUrl={
              product.component === 'product_v2'
                ? (product as unknown as ProductV2Storyblok).ogImage.filename
                : product.thumbnail.filename
            }
            quantity={purchasingController.quantity}
            title={purchasingController.product?.name ?? ''}
          />
        </div>

        {addresses ? (
          <div className="flex flex-col space-y-2">
            <div className="text-xs">{t('drawers.review.draw.shipTo')}</div>
            <ShippingAddressButton
              addresses={addresses.addresses}
              onChange={(edit) => {
                if (!edit) {
                  goToStep('add_shipping_address');
                } else {
                  goToStep('select_shipping');
                }
              }}
              selectedAddressId={selectedAddressId}
            />
          </div>
        ) : null}

        <CustomsCode
          address={selectedAddress}
          onClick={() => {
            goToStep('edit_shipping_address');
          }}
        />

        <div className="flex flex-col space-y-2">
          <div className="text-xs">{t('drawers.review.draw.payWith')}</div>
          <PaymentMethodButton
            isCryptoComEnabled={false}
            isPreOrder={false}
            paymentErrorCode={null}
            paymentMethods={paymentMethods?.listPaymentMethods ?? []}
            selectedPaymentMethodId={selectedPaymentMethodId ?? ''}
            selectedPaymentType={PaymentType.CARD}
            usingNewPaymentMethod={isNewPaymentMethod}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <div className="flex justify-between">
            <div>{t('drawers.offer.review.offer')}</div>
            <div>
              {workingOrLoading ? (
                <Skeleton className="h-full w-24" />
              ) : (
                <ProductPrice
                  currencyCode={data?.offer?.currencyCode as CurrencyCode}
                  price={Number(data?.offer?.amount)}
                  skipConvertCurrency
                  skipTaxCalculation
                />
              )}
            </div>
          </div>
          <div className="flex justify-between font-bold">
            <div>{t('drawers.offer.review.total')}</div>
            <div>
              {workingOrLoading ? (
                <Skeleton className="h-full w-24" />
              ) : (
                <ProductPrice
                  currencyCode={data?.offer?.currencyCode as CurrencyCode}
                  price={Number(data?.offer?.amount)}
                  skipConvertCurrency
                  skipTaxCalculation
                />
              )}
            </div>
          </div>
          <div className="flex justify-between text-neutral-4">
            {t('drawers.offer.review.includesTax')}
          </div>
          <div className="flex justify-between text-neutral-4">
            {t('drawers.offer.review.additionalTaxes')}
          </div>
        </div>

        <div className="!mt-6 flex flex-col space-y-3">
          <Button
            disabled={!readyToConfirm}
            fullWidth
            label={t('drawers.offer.review.save')}
            loading={workingOrLoading}
            onClick={() => void handleSubmitOffer()}
            size="lg"
            variant="primary"
          />
        </div>

        <div className="px-6 text-center text-xs text-neutral-4">
          {t('drawers.offer.review.footer.legalDisclaimerOnEnter')}
        </div>
      </div>
    </div>
  );
};
